import { Fragment, type ReactNode } from 'react';
import type { LegalBlock, LegalDocument } from './types';
import './legal.css';

/**
 * Ссылки и почта в тексте документа — делаем их кликабельными.
 *
 * Адрес обязан заканчиваться буквой или цифрой: в документе он почти всегда
 * стоит в конце предложения, и точка иначе уезжает внутрь ссылки — по такому
 * mailto письмо просто не уйдёт.
 */
const LINK_RE = /(https?:\/\/[^\s,;)]*[^\s,;).]|[\w.+-]+@[\w-]+\.[\w.-]*\w)/g;

function linkify(text: string): ReactNode[] {
  return text.split(LINK_RE).map((part, i) => {
    if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>;
    const href = part.includes('@') ? `mailto:${part}` : part;
    return (
      <a key={i} href={href} target="_blank" rel="noreferrer noopener">
        {part}
      </a>
    );
  });
}

/**
 * Страница юридического документа.
 *
 * Идущие подряд маркированные пункты собираем в один <ul> — в исходном .docx
 * это плоская последовательность абзацев, а разметке нужен список.
 */
export function LegalPage({ doc }: { doc: LegalDocument }) {
  return (
    <article className="legal">
      <h1 className="legal__title">{doc.title}</h1>
      {groupBullets(doc.blocks).map((group, i) =>
        Array.isArray(group) ? (
          <ul className="legal__list" key={i}>
            {group.map((item, j) => (
              <li key={j}>{linkify(item.text)}</li>
            ))}
          </ul>
        ) : group.kind === 'heading' ? (
          <h2 className="legal__heading" key={i}>
            {group.number && <span className="legal__num">{group.number}</span>}
            {group.text}
          </h2>
        ) : (
          <p className="legal__para" key={i}>
            {group.number && <span className="legal__num">{group.number}</span>}
            {linkify(group.text)}
          </p>
        )
      )}
    </article>
  );
}

type Bullet = Extract<LegalBlock, { kind: 'bullet' }>;
type Grouped = Exclude<LegalBlock, Bullet> | Bullet[];

function groupBullets(blocks: LegalBlock[]): Grouped[] {
  const out: Grouped[] = [];
  for (const block of blocks) {
    if (block.kind !== 'bullet') {
      out.push(block);
      continue;
    }
    const last = out[out.length - 1];
    if (Array.isArray(last)) last.push(block);
    else out.push([block]);
  }
  return out;
}
